import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../utils/api';

interface User {
  id: string;
  email: string;
  name: string;
  role?: string;
  isAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<any>;
  verifyTotpLogin: (challengeToken: string, code: string) => Promise<any>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  authLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  const bootstrapAuth = async () => {
    try {
      const response = await api.get('/api/auth/profile');
      const profile = response.data;
      if (profile) {
        setUser({
          id: profile._id || profile.id,
          email: profile.email,
          name: profile.name,
          role: profile.role,
          isAdmin: true
        });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    bootstrapAuth();

    const handleTokenExpired = () => {
      setUser(null);
    };

    window.addEventListener('tokenExpired', handleTokenExpired);
    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired);
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/api/auth/login', { email, password });
      if (response?.data?.requiresTotp) {
        return response.data;
      }
      if (response?.data?.requiresTotpSetup) {
        return response.data;
      }
      const userData = response?.data?.user;

      if (!userData) {
        throw new Error('Invalid admin response from server');
      }

      setUser(userData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message || 'Login failed');
    }
  };

  const verifyTotpLogin = async (challengeToken: string, code: string) => {
    try {
      const response = await api.post('/api/auth/login/totp', { challengeToken, code });
      const userData = response?.data?.user;
      if (!userData) {
        throw new Error('Invalid TOTP login response from server');
      }
      setUser(userData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message || 'TOTP verification failed');
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // No-op; clear local state even if request fails.
    } finally {
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    login,
    verifyTotpLogin,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    authLoading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
